/**
 * en —— 跟 zh-HK **逐 key 對齊**（缺／多 key 都會被擋：typecheck ＋ `check:locales`）
 * ⚠️ 唔准加 `as const`：加咗會變字面型別，`typeof zhHK` 檢查會誤報 ✗
 */
export const en = {
  app_name: 'CoSingery',
  app_tagline: 'Happy, hurting, or just want to remember — leave yourself a song',

  skeleton_title: 'Skeleton is in place',
  skeleton_body:
    'What exists today: three languages, design tokens and the build gates. Conversation and song generation arrive brick by brick (safety layer first).',
  skeleton_gate_note:
    'Before shipping: build, locale key parity and shared-solutions parity must all pass.',

  language_label: 'Language',
  locale_zh_hk: '繁體中文（香港）',
  locale_en: 'English',
  locale_zh_cn: '简体中文',

  nav_chat: 'Chat',
  nav_songs: 'My songs',

  chat_placeholder: 'Say anything…',
  chat_send: 'Send',
  chat_thinking: 'One moment…',
  chat_retry: 'Try again',
  chat_quote: 'Quote',
  song_lang_label: 'Song language',
  song_lang_cantonese: 'Cantonese',
  song_lang_mandarin: 'Mandarin',
  song_lang_english: 'English',
  song_generate_button: 'Generate the song (1–3 min)',


  chat_empty_title: 'Some moments pass. But that song is always yours.',
  chat_empty_body:
    'CoSingery does not teach you how to write a song, and it will not write one for you. It is a quiet room, with a pen and a melody in it — a place to keep today\'s you: happy, heartbroken, angry, stirred, missing someone…',
  chat_empty_action: 'Start',
  chat_hold_note: 'We keep talking for now; the song comes later.',
  chat_error_stream: 'The connection dropped. You can try again.',
  chat_error_model: 'I cannot reach the model right now — please try again later.',

  songs_empty_title: 'No songs yet',
  songs_empty_body: 'Once we talk, your song will show up here.',
  songs_not_built: 'This page is not built yet (song generation is the next brick).',
  songs_library_count: '{{n}} songs — your playlist holds all of them',
  new_chat_button: '+ New chat',
  continue_chat_button: 'Keep chatting',
  new_chat_hint: 'A new chat never deletes your songs — they all stay in "My songs".',
  new_chat_will_start: 'The song is done — your next message starts a new chat (tap "Keep chatting" to stay).',

  song_generate: 'Make a song',
  song_generating: 'Writing the lyrics…',
  song_ready: 'Your song is ready',
  song_toggle: 'Show full lyrics',
  song_status_label: 'Status',
  song_hold_notice: 'Let us talk a bit more before the song.',
  song_need_more_notice: 'I want the song to be really yours — tell me a bit more about what happened?',
  song_error_generic: 'I cannot write a song right now — please try again later.',
  song_error_too_generic: 'That came out too generic — tell me a bit more and I will use your real details.',
  song_error_repetitive: 'The lyrics repeated too much — tell me a bit more so there is something new to write.',
  song_error_blocked: 'What came out is not right — let us try a different angle.',
// ── Three-step flow: ① chat → ② lyrics (editable) → ③ make the song ──
song_step_1: '① Chat',
song_step_2: '② Lyrics',
song_step_3: '③ Song',
song_write_lyrics: 'Write the lyrics',
lyrics_review_title: 'Have a look — is this right?',
lyrics_review_hint: 'Change any words or details. After your edits it is truly your song.',
lyrics_review_title_label: 'Title',
lyrics_review_lyrics_label: 'Lyrics',
lyrics_review_save: 'Save changes',
lyrics_review_saved: 'Saved',
lyrics_review_regenerate: 'Write it again',
lyrics_review_confirm: 'Confirm & make the song',
lyrics_review_confirm_note: 'Making the song takes about 1–3 minutes (real music generation).',
song_render_pending: 'Making your song… (about 1–3 minutes)',
song_edit_error_short: 'That is a bit short — add a few more lines.',
song_edit_error_blocked: 'These lyrics are not suitable for a song — please try writing it another way.',
// ── Variant picking (Mureka returns 2 versions per generation) ──
variant_label: 'Version',
variant_use: 'Use this one',
variant_picked: 'In use',
variant_hifi: 'Hi-fi',
/** ⭐ 1.1.0 (2026-09-23): tap a card to choose it (no separate button) + share per version */
variant_tap_hint: 'Tap a version to choose it — you can preview and share each one',
/** ⭐ 2026-09-23: when there is no audio yet we say so (the ▶ used to do nothing) */
song_audio_pending: 'Audio is still being generated…',
song_audio_not_ready: 'This song has no audio yet (it may still be generating) — try ▶ again in a moment',
  /** ⭐ 2026-09-23: per-song switch for singing in your own cloned voice */
  voice_mine_on: 'Sing in my voice',
  voice_mine_off: 'Use default voice',
  /* ⭐ **2026-09-26（`decisions/051`）**：vocal gender */
  gender_auto: 'Auto',
  gender_male: 'Male',
  gender_female: 'Female',
// ── Must-use box (select text → pin it into the song) ──
must_use_hint: 'This must go into the song:',
  must_use_howto: 'Tap one above first, then select the words below',
  must_use_armed: 'On — selecting words marks them; tap again to stop',
  must_use_marked_count: '{{n}} marked for the song',


must_use_exact: 'Exactly these words',
must_use_meaning: 'This meaning (rewording ok)',
must_use_remove: 'Remove pin',
// ── Player (bottom tabs + persistent mini bar) ──
tab_chat: 'Chat',
tab_songs: 'My songs',
tab_player: 'Player',
player_now_playing: 'Now playing',
player_play: 'Play',
player_pause: 'Pause',
player_prev: 'Previous',
player_next: 'Next',
player_seek: 'Seek',
player_hifi_on: 'Hi-fi (flac) on',
player_hifi_off: 'Standard (mp3)',
player_error: 'Playback error',
player_empty_title: 'No songs to play yet',
player_empty_body: 'Have a chat first — write a song that is yours.',
  songs_list_failed: 'Could not load your songs. Please try again later.',

  /*
   * ⚠️ 情緒應對（`decisions/009` 乙案）—— **待產品負責人過目（P-25）**
   * Default = keep talking, keep making the song; only **imminent** danger uses the three lines below.
   * ❌ 空洞話（「cheer up」／「I understand how you feel」）；❌ 唔准承諾保密或永遠在場
   */
  // ── ⭐ 2026-09-22 產品負責人 UI／UX 五改（`decisions/023`）──
  send_aria: 'Send',
  topbar_new_chat_aria: 'New chat',
  topbar_manage_lyrics: 'Manage lyrics',
  topbar_start_song: 'Start Creating Song',
  keep_going_toggle: 'Keep chatting',
  sheet_close_aria: 'Close',
  sheet_start_song_title: 'Write a song',
  sheet_lyrics_title: 'Lyrics',
  /** ⭐ 語體（`decisions/030`）：市場書面（預設）／口語 */
  lyrics_sheet_pick_register: 'How should it sound?',
  register_cantopop: 'Chart-style (written)',
  register_spoken: 'Spoken (your own words)',
  register_hint: 'Chart-style uses written language with short lines (like radio songs). Spoken keeps your own wording.',
  lyrics_sheet_pick_lang: 'Which language?',
  lyrics_sheet_edit: 'Edit by hand',
  lyrics_sheet_cancel: 'Cancel edit',
  lyrics_sheet_later: 'Later',
  must_use_exact_short: 'Exact',
  must_use_meaning_short: 'Meaning',
  must_use_howto_short: 'Tap a chip, then select words',

  understanding_start: 'Start (I will say back what I heard)',
  understanding_ok: 'Yes, write it',
  understanding_fix: 'Change it',
  lyrics_copy: 'Copy',
  share_button: 'Share',
  remake_button: 'New style',
  revise_hint: 'Tell me what to change (e.g. second verse, use my own tone)',
  remake_hint: 'What style? (e.g. slower, piano, no drums)',
  revise_placeholder: 'Which line, and how?',
  remake_placeholder: 'What style?',
  history_title: 'Earlier chats',
  history_empty: 'No other chats yet.',
  history_untitled: '(no messages)',
  history_songs: '{{n}} songs',
  working_hint: 'Working… (please stay on this screen)',

  edit_hint: 'Tap the lines you want changed (more than one is fine), then type what you want below',
  edit_line_placeholder: 'What should this line become?',
  edit_apply_ai: 'Let AI rewrite',
  edit_apply_direct: 'Apply directly',
  edit_done: 'Done',
  pick_hint: 'Tap the sentences above that must go into the song (tap again to undo)',
  style_title: 'What style?',
  style_start: 'Generate (costs money)',
  /** ⭐ 1.1.0: quick list + "all styles" (`decisions/034`) */
  style_quick_label: 'Quick picks (you can add or remove)',
  style_show_all: 'All styles ({{n}})',
  style_collapse: 'Show less',
  style_quick_add: 'Add to / remove from quick picks',
  style_slow: 'Slow ballad',
  style_upbeat: 'Upbeat',
  style_piano: 'Piano',
  style_guitar: 'Acoustic guitar',
  style_drums: 'Drums',
  style_nodrums: 'No drums',
  style_lofi: 'Lo-fi',
  style_jazz: 'Jazz',
  style_electronic: 'Electronic',
  style_strings: 'Strings',
  style_rock: 'Rock',
  style_folk: 'Folk',
  go_player: 'Player',
  share_song_text: 'I wrote this song with CoSingery',
  share_file_fallback_note: 'Your browser cannot share the file directly — sharing the link instead',
  /** ⭐ 2026-09-23 (share-button 1.2.0): sharing must respond, never wait forever, and be honest on failure */
  share_preparing: 'Preparing the file…',
  share_timeout_note: 'The file was too slow / could not be fetched — fell back to text + link (tap again to retry)',
  share_gesture_note: 'The system refused file sharing (usually because it waited too long) — fell back to text + link',
  mode_listener: 'Listener',
  mode_chat: 'Chat',
  mode_switch_aria: 'Conversation mode',
  chat_mode_hint: 'In this mode you can talk about anything — your song still gets written.',
  chat_mode_placeholder: 'Say anything…',
  diary_page_aria: 'Diary',
  diary_done_only: 'Finished songs only',
  diary_write_label: 'What I want to remember',
  diary_write_placeholder: 'Write down what is on your mind… (saved automatically)',
  diary_saving: 'Saving…',
  diary_saved: 'Saved',
  diary_save_failed: 'Could not save — you can retry',
  diary_retry: 'Retry',
  diary_lyrics_title: 'Lyrics of this song',
  diary_no_audio: 'No music for this one yet (you can press “Make song” in the chat)',
  diary_empty_title: 'The diary has not started',
  diary_empty_body: 'Once a song is finished, a page appears here — with the date, how you felt, the lyrics and the player.',
  history_rename: 'Rename',
  /** ⭐ 0016（`decisions/039`）: rename the song (✎ next to the title on the diary page) */
  song_rename: 'Rename song',
  song_rename_title: 'Name this song',
  song_rename_placeholder: 'e.g. Last train and moonlight',
  song_rename_empty: 'The song name cannot be empty',
  history_delete: 'Delete',
  history_rename_title: 'Name this conversation',
  history_name_placeholder: 'e.g. the thing with my mum',
  history_save: 'Save',
  history_cancel: 'Cancel',
  history_delete_title: 'Delete this conversation?',
  history_delete_warning: 'The messages will be deleted and cannot be restored. (Your songs stay in My Songs.)',
  history_delete_confirm: 'Delete',
  history_rename_failed: 'Could not rename',
  history_delete_failed: 'Could not delete',
  diary_title: 'My Feeling Diary',
  diary_prev: 'Previous page',
  diary_next: 'Next page',
  tab_me: 'Mine',
  me_identity_title: 'Identity',
  me_identity_note: 'You are using a device identity (not signed in) — if you change phone or clear browser data, we cannot find your songs again. Sign-in is a later step.',
  me_identity_id: 'Device id',
  me_billing_title: 'Payments (Hong Kong online payment)',
  me_credits: 'Credits left (how many songs you can make)',
  me_billing_provider: 'Payment status',
  me_billing_ready: 'Connected',
  me_billing_not_ready: 'Not connected',
  me_billing_no_pricing: 'No pricing yet',
  me_billing_needs: 'To switch on Hong Kong online payment we need from you (the product owner):\n1. merchant account / method (FPS, card, PayPal, PayMe…)\n2. price per song or bundle (HK$)\n3. refund policy\n4. the provider API key (stored only as a Cloudflare secret, never in code)',
  me_billing_refund_note: '⚠️ Nothing is charged while payment is not connected; once it is, every order will be listed above.',
  me_orders_title: 'Orders',
  me_orders_empty: 'No orders yet',
  /** ⭐ 0017 (`decisions/041`): **sign-in (email magic link) + free trial + credits** */
  login_title: 'Welcome to CoSingery',
  login_subtitle: 'Just your email — your first song is on us.',
  login_email_label: 'Your email',
  login_email_placeholder: 'you@example.com',
  /** ⭐ `decisions/044` / `045` 0020d: **two buttons** (the box below auto-detects code vs invite) */
  login_send_email_only: 'Sign in with email',
  login_send_with_invite: 'Sign in with invite code',
  login_error_invite_required_hint: 'That email is not registered — enter your invite code and try again.',
  login_sending: 'Sending…',
  login_sent_notice: 'Sent to {{email}} — type the 6-digit code below to sign in.',
  login_sent_hint: 'Not there? Check spam. (Only the newest email works.)',
  /** ⭐ 0020 (`decisions/045`): **6-digit code** (auto-detected; auto sign-in at 6 digits) */
  login_code_label: '6-digit code',
  login_code_placeholder: '123456',
  login_code_busy: 'Checking…',
  login_need_email_short: 'Enter your email first, then the code.',
  login_code_error_invalid: 'That code is not right, or it has been used or expired — you can send a new one.',
  login_code_error_network: 'Network problem — please try again.',
  login_devlink_label: '(local dev) sign-in link:',
  login_error_email_invalid: 'That email does not look right — please check it again.',
  login_error_send_failed: 'We cannot send email right now — please try again later.',
  login_error_too_many: 'Too many attempts — please wait a moment.',
  login_error_link_invalid: 'That sign-in link has been used or has expired — send a new one.',
  login_error_generic: 'Something went wrong while signing in',
  login_privacy_note: 'No marketing — your email is only used to sign in and for receipts.',
  /** ⭐ 0018 (`decisions/042`): **Beta by invitation** */
  login_beta_notice: '🧪 Testing period: invite code required, one free song each, nothing charged yet.',
  login_invite_label: 'Invite code',
  login_invite_placeholder: 'INV-XXXX-XXXX-XXXX',
  login_error_invite_required: 'This testing period needs an invite code — please enter one (or message me and I will send one).',
  login_error_invite_invalid: 'That invite code is wrong, already used, or expired — message me and I will send a new one.',
  login_error_invite_mismatch: 'That invite code is already tied to another email — you will need a new code for this email.',
  login_direct_link_notice: '✅ Invite code accepted — tap the link below to sign in (valid for 15 minutes, one use only):',
  /** ⭐ 0019 (`decisions/043`): **feedback + company details** */
  feedback_title: 'Send feedback (Beta)',
  feedback_privacy_note:
    '⚠️ Only the text you type is sent (no lyrics or diary attached). Only the product owner reads it — it shapes the next version.',
  feedback_kind_ux: 'Experience',
  feedback_kind_bug: 'Problem',
  feedback_kind_idea: 'Idea',
  feedback_kind_other: 'Other',
  feedback_score_label: 'Overall',
  feedback_placeholder: 'Where did you get stuck? What do you want most? (a line or two is enough)',
  feedback_send: 'Send',
  feedback_sending: 'Sending…',
  feedback_cancel: 'Later',
  feedback_thanks: 'Got it — thank you 🙏 (I read these and use them for the next version)',
  feedback_error_empty: 'Please write a line before sending.',
  feedback_error_too_many: 'That is enough for today — tomorrow is fine too.',
  feedback_error_generic: 'Could not send right now',
  me_feedback_button: 'Send feedback',
  me_feedback_hint: 'Testing period: your feedback directly shapes the next version.',
  legal_provider: 'Service provider',
  legal_br: 'Business Registration (BR)',
  legal_address: 'Address',
  legal_email: 'Contact',
  legal_missing: 'To be provided',
  legal_policies_pending: '(Terms / refund policy / privacy policy: in preparation)',
  legal_load_failed: 'Could not load the service provider details',
  pay_payee_label: 'Payee',
  me_logged_in_as: 'Signed in as',
  me_logout: 'Sign out',
  me_credits_title: 'Songs left',
  me_credits_left: 'You can make {{n}} more song(s)',
  me_credits_none: 'No songs left — buy a pack to keep creating.',
  me_credits_free_trial: '(1 of these is your free trial)',
  me_topup_cta: 'Buy a pack',
  pack_p10: '10 songs',
  pack_p25: '25 songs',
  pack_p75: '75 songs',
  render_variants_one: '1 version',
  render_variants_two: '2 versions (pick one)',
  song_error_insufficient_credits: 'No songs left — buy a pack on the Me page to keep creating.',
  song_error_login_required: 'Please sign in first to create a song.',
  me_about_title: 'About',
  /** ⭐ 2026-09-23: restore my songs (after clearing browser data / switching device) */
  restore_title: 'Restore my songs',
  restore_warn:
    '⚠️ This app currently uses a "device identity" (no login yet): **clearing browser data / switching device** empties this device\'s library (the songs are still on the server). Save the code below to recover it.',
  restore_code_label: 'Your recovery code',
  restore_no_code: '(unavailable)',
  restore_copy: 'Copy recovery code',
  restore_copied: 'Copied — keep it in your notes / password manager',
  restore_input_placeholder: 'Paste your old recovery code',
  restore_button: 'Restore',
  restore_busy: 'Checking…',
  restore_invalid: 'That code looks wrong (expected 16–64 letters/digits/dashes)',
  restore_failed_empty: 'No songs found under that recovery code (your current data was not changed)',
  restore_failed_offline: 'Could not check right now (network) — please retry (your data was not changed)',
  restore_ok: 'Restored ✓ loading your songs…',
  restore_emergency_hint: '⚠️ If you cannot see "Restore my songs" here (outdated app), open the emergency page:',
  me_build_label: 'Build',
  me_build_unknown: 'unavailable',
  /** ⭐ 2026-09-23#3 (B1): share capability probe */
  me_share_probe_label: 'Share support',
  me_share_probe_legend: '(📄 file / 🖼 image / 💬 text / 📱 installed app; ✓ yes, ✗ no)',
  /** ⭐ 2026-09-23#3 (B2): on iOS the Home Screen app and Safari keep separate storage */
  restore_card_title: 'Added to Home Screen? / Coming from Safari?',
  restore_card_body:
    'On iPhone the Home Screen app and Safari use **separate storage** (separate identities) — paste your recovery code here and your songs come right back.',
  restore_ios_note:
    'iPhone: tap Copy code in Safari → open the Home Screen app → paste it here (Apple does not let links open Home Screen apps; Android does).',
  /** ⭐ 2026-09-23#3: share failures must state the reason */
  share_failed_note: 'Sharing did not succeed (system reported)',
  share_file_unsupported_note: 'This device cannot share files — falling back to text/link only.',
  /** ⭐ 2026-09-23#4: show how many songs THIS device currently has */
  restore_own_count: '(this device: {{n}} songs)',
  restore_own_count_unknown: '(unavailable)',
  restore_which_code_hint:
    'Note: the "Your recovery code" above is what **this device** is using now. To get your old songs back, paste the **other** code (the one shown in Safari / on your old device).',
  restore_failed_same_code:
    '⚠️ That is **this device\'s current** code (0 songs) — paste the **old code that has your songs**, not the one above.',
  /** ⭐ 2026-09-23#5 (`decisions/031`): public share page + share the whole page */
  share_page_button: 'Share this page',
  share_page_creating: 'Creating share link…',
  share_page_created: 'Share link created (expires in {{days}} days) — share or copy it',
  share_page_share: 'Share link',
  share_page_copy: 'Copy link',
  share_page_copied: 'Link copied',
  share_page_revoke: 'Revoke',
  share_page_revoked_ok: 'Revoked (the link stops working immediately)',
  share_page_includes: 'The share page shows: date / song title / player / lyrics / your feeling notes (⚠️ anyone with the link will see these)',
  share_page_text: 'I have a My Feeling Diary page to share with you:',
  share_page_shared: 'Handed to the system share sheet',
  share_page_share_failed: 'Could not share (try Copy link)',
  share_page_from: 'A friend shared this song with you',
  share_page_meta: 'Viewed {{views}} times',
  share_page_not_found: 'This share link does not exist (maybe a typo)',
  share_page_revoked: 'This share has been revoked 🚫',
  share_page_expired: 'This share has expired',
  share_page_failed: 'Cannot show this share right now',
  /** ⭐ 2026-09-23#6: multiple versions → state which one is shared (only the picked one plays) */
  share_page_version: 'Version {{n}} of {{total}}',
  me_version: 'version',
  me_load_failed: 'Could not load',
  me_refresh: 'Retry',
  me_methods_not_set: 'No payment QR set yet (the owner needs to add the FPS / AlipayHK / WeChat Pay QR images).',
  me_manual_pick_pack: 'Choose a bundle',
  me_manual_pick_method: 'Choose how to pay',
  me_manual_create: 'Create order',
  me_manual_reference: 'Reference',
  me_manual_pay_hint: 'Pay with the QR above and put the reference in the note — it makes matching much faster.',
  me_manual_pending: 'Status: waiting for your payment',
  me_manual_claimed: 'Status: you said you paid — waiting for confirmation (usually within hours)',
  me_manual_paid: 'Status: credited ✅ (credits added)',
  me_manual_claim_button: 'I have paid',
  me_manual_confirm_note: '⚠️ Personal QR codes give us no automatic notification — we check by hand (usually within hours) and only then add the credits.',
  /* ── ⭐ 2026-09-26: **owner-only** (confirm payments; requires `OWNER_EMAILS`) ── */
  me_owner_queue_title: 'Waiting for your confirmation',
  me_owner_queue_empty: 'Nothing waiting 👍',
  me_owner_confirm_button: 'Confirm payment received (add credits)',
  me_owner_confirmed: 'Confirmed ✅',
  me_owner_queue_hint: 'Only “claimed / pending” orders are listed; confirming credits the buyer account immediately (never twice).',
  /* ── ⭐ Playlists / mood categories (0013；`decisions/033`；PO 2026-09-24) ── */
  /** The 12 mood / purpose categories (slugs live in `src/playlists.ts`) */
  pl_favourite: 'Favourites',
  pl_happy: 'Happy',
  pl_sad: 'Sad',
  pl_excited: 'Excited',
  pl_celebrate: 'Celebration',
  pl_to_my_love: 'To my love',
  pl_missing: 'Missing you',
  pl_gratitude: 'Gratitude',
  pl_healing: 'Healing',
  pl_cheer: 'Courage',
  pl_proud: 'Proud',
  pl_angry: 'Angry / not letting go',
  pl_fun: 'Fun',
  /** Add to playlist (next to Share in chat messages / the diary page) */
  playlist_add_button: 'Add to playlist',
  playlist_sheet_title: 'Add to which playlist?',
  playlist_sheet_hint: 'You can pick more than one — the categories are yours to choose; we never sort your songs for you.',
  playlist_added_note: 'Added to “{{name}}”',
  playlist_already_note: '“{{name}}” already had this song',
  playlist_removed_note: 'Removed from “{{name}}”',
  playlist_not_in_note: 'It was not in that playlist',
  playlist_untitled: 'Untitled playlist',
  playlist_membership_failed: 'Could not read which playlists this song is in',
  playlist_action_failed: 'Could not do it ({{error}})',
  playlist_new_label: 'Start a new playlist',
  playlist_new_placeholder: 'e.g. For my run',
  playlist_new_button: 'Add',
  playlist_new_failed: 'Could not create it ({{error}})',
  playlist_done: 'Done',
  playlist_count: '{{n}} songs',
  playlist_all_songs: 'All songs',
  playlist_load_failed: 'Could not load this playlist',
  player_play_all: '▶ Play all',
  /** ⭐ 2026-09-24: player tab (playlist → song → player → flip left for lyrics) */
  player_diary_label: 'What you wrote',
  player_no_lyrics: 'No lyrics for this song yet',
  player_deck_aria: 'Player / lyrics (flip left for the lyrics)',
  player_back_to_list: 'Back to the list',
  /** ⭐ 2026-09-24: basic player functions (repeat / shuffle / favourite) */
  player_repeat_off: 'No repeat (stops at the end)',
  player_repeat_one: 'Repeat one',
  player_repeat_all: 'Repeat all',
  player_shuffle_on: 'Shuffle: on',
  player_shuffle_off: 'Shuffle: off',
  player_fav_add: 'Add to favourites',
  player_fav_remove: 'Remove from favourites',
  player_sequence_note: 'Songs play one after another (it stops at the end)',
  diary_mode_diary: 'Diary',
  diary_mode_list: 'List',
  diary_mode_switch_aria: 'View (diary / list)',
  diary_play_through: '▶ Play through',
  /** ⭐ 2026-09-24: diary page number (like leafing through a book) */
  diary_page_of: 'Page {{n}} of {{total}}',
  /** ⭐ `page-flip` 1.2.0: page corner hints (like a real book) */
  flip_hint_prev: 'Turn back',
  flip_hint_next: 'Turn the page',
  /** ⭐ 2026-09-24: journal length cap (anti-abuse only; normal writing never hits it) */
  diary_too_long: 'That entry is too long (limit {{max}} characters) — you can split it into a few entries',
  playlist_empty: 'No songs in this playlist yet',
  playlist_empty_hint: 'Go to My songs or the chat and tap “Add to playlist”',
  /* ── ⭐ 0014 音樂風格（`decisions/034`；產品負責人 2026-09-24）── */
  genre_label: 'Music style',
  genre_auto: 'Auto (AI picks)',
  genre_self: 'Choose myself',
  genre_auto_note: 'AI picks a style that fits what you said; tap the right one to choose yourself.',
  genre_auto_picked: 'AI picked: {{name}}',
  genre_kpop: 'K-POP',
  genre_cantopop: 'Cantopop (HK pop)',
  genre_mandopop: 'Mandopop',
  genre_ballad: 'Ballad',
  genre_piano: 'Piano version',
  genre_acoustic: 'Acoustic guitar',
  genre_rock: 'Rock',
  genre_rap: 'Rap / hip-hop',
  genre_rnb: 'R&B',
  genre_edm: 'EDM',
  genre_lofi: 'Lo-fi',
  genre_jazz: 'Jazz',
  genre_citypop: 'City pop',
  genre_dreampop: 'Dream pop',
  genre_indie: 'Indie',
  genre_folk: 'Folk',
  genre_country: 'Country',
  genre_blues: 'Blues',
  genre_soul: 'Soul',
  genre_funk: 'Funk',
  genre_disco: 'Disco',
  genre_house: 'House',
  genre_techno: 'Techno',
  genre_trap: 'Trap',
  genre_dnb: 'Drum & Bass',
  genre_reggae: 'Reggae',
  genre_latin: 'Latin',
  genre_bossanova: 'Bossa nova',
  genre_ambient: 'Ambient',
  genre_chill: 'Chill',
  genre_unplugged: 'Unplugged',
  genre_metal: 'Metal',
  genre_punk: 'Punk',
  genre_synthwave: 'Synthwave',
  genre_orchestral: 'Orchestral',
  genre_classical: 'Classical',
  genre_musical: 'Musical',
  genre_anime: 'Anime',
  genre_chinesestyle: 'Chinese style',
  genre_lullaby: 'Lullaby',
  safety: {
    hold_intro:
      "I heard what you said. I am not going to rush on to anything else — I want to stay with you for a bit.",
    hold_ask:
      'You do not have to be on your own right now — is there anyone you can reach straight away? (family, a friend, a colleague)',
    hold_song_later:
      'Let us keep talking. When you are ready, I will make you a song, and we will see if it helps.',
  },

  /* ⭐ 2026-10-11 UI v2 new keys (source: design/strings.js NEW; design R-03 "new·pending approval") */
  menu_open_aria: 'Open menu',
  drawer_aria: 'Menu',
  nav_playlists: 'Playlists',
  nav_settings: 'Settings',
  settings_title: 'Settings',
  settings_back_aria: 'Back',
  settings_font_size: 'Text size',
  settings_font_size_hint: 'Handwritten titles, lyrics and diary scale together with buttons and lists.',
  font_scale_1: 'Small',
  font_scale_2: 'Standard',
  font_scale_3: 'Large',
  font_scale_4: 'X-Large',
  font_scale_5: 'Largest',
  font_scale_preview: 'There is something I want to say, slowly.',
  settings_theme: 'Appearance',
  theme_light: 'Day',
  theme_dark: 'Night',
  theme_system: 'System',
  settings_notify: 'Notifications',
  notify_song_ready: 'Tell me when my song is ready',
  notify_song_ready_hint: 'Only this one — we will never nudge you to come back.',
  settings_account: 'Account & credits',
  settings_privacy: 'Privacy & anonymity',
  privacy_anon_note: 'Your chats, lyrics and diary are yours alone. Shared pages never show your email or account.',
  settings_export: 'Export diary',
  export_hint: 'Download every diary page (date, feelings, lyrics) as one file.',
  export_button: 'Export',
  gen_stage_aria: 'Song progress',
  gen_stage_listen: 'Listening to you',
  gen_stage_lyrics: 'Writing lyrics',
  gen_stage_compose: 'Composing',
  gen_stage_mix: 'Mixing',
  gen_keep_chatting: 'Keep talking — it will appear right here when ready.',
  player_open_aria: 'Open player',
  player_speed: 'Speed',
  player_volume: 'Volume',

  /* ⭐ 2026-10-11 (decisions/053): bottom tab bar — 3 tabs */
  tab_diary: 'Feeling Diary',
  tab_playlists: 'Playlists',
};
